import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ConstantService } from 'src/app/shared/constant/constant.service';
import {
  BehaviorSubject,
  Observable,
  firstValueFrom,
} from 'rxjs';

import {
  LoginResponse,
  LoginUser,
  Plan,
  WebsiteAnalyze,
} from '../../../shared/models/user.models';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { filter, map, tap } from 'rxjs/operators';
import { WebsiteAnalyzeService } from 'src/app/services/common/website-analyze/website-analyze.service';
import { ValidationService } from '../../validation.service';
import { TokenStorageService } from './token-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  logout_success: string;
  logout_msg: string;
  loginData1: any;
  nextUrl: string;
  googleLoginOptions = {
    scope: 'profile email',
  };

  private userLoggedSubject: BehaviorSubject<Plan> = new BehaviorSubject<Plan>(
    undefined
  );
  private userLoggedPlanSubject: BehaviorSubject<Plan> =
    new BehaviorSubject<Plan>(undefined);
  private userLoggedNbWebsitesSubject: BehaviorSubject<number> =
    new BehaviorSubject<number>(undefined);
  private updateUserLoggedNbWebsitesSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(undefined);
  private userLoggedWebSitesAnalyseSubject: BehaviorSubject<
    Array<WebsiteAnalyze>
  > = new BehaviorSubject<Array<WebsiteAnalyze>>(undefined);

  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private websiteAnalyzeService: WebsiteAnalyzeService,
    private validationService: ValidationService,
    private tokenStorage: TokenStorageService,
  ) {}

  private purgeLegacyAuthKeys(): void {
    [
      ConstantService.localStorageKeys.access_Token,
      ConstantService.localStorageKeys.accessToken,
      ConstantService.localStorageKeys.refresh_Token,
      ConstantService.localStorageKeys.refreshToken,
      ConstantService.localStorageKeys.access_Token_Google,
      ConstantService.localStorageKeys.role,
    ].forEach((key) => localStorage.removeItem(key));
  }

  removeToken() {
    this.tokenStorage.clearAuth();
  }

  getToken() {
    return this.tokenStorage.getAccessToken();
  }

  setToken(token: string, userId?: string) {
    this.tokenStorage.setAccessToken(token, userId);
  }

  get userLoggedObs(): Observable<Plan> {
    return this.userLoggedSubject.asObservable();
  }
  set userLogged(userLogged: Plan) {
    this.userLoggedSubject.next(userLogged);
  }

  get userLoggedPlanObs(): Observable<Plan> {
    return this.userLoggedPlanSubject
      .asObservable()
      .pipe(filter((data) => data !== undefined));
  }
  set userLoggedPlan(plan: Plan) {
    this.userLoggedPlanSubject.next(plan);
  }

  get userLoggedNbWebsitesObs(): Observable<number> {
    return this.userLoggedNbWebsitesSubject
      .asObservable()
      .pipe(filter((data) => data !== undefined));
  }
  set userLoggedNbWebsites(nb: number) {
    this.userLoggedNbWebsitesSubject.next(nb);
  }
  get userLoggedWebSitesAnalyseObs(): Observable<Array<WebsiteAnalyze>> {
    return this.userLoggedWebSitesAnalyseSubject
      .asObservable()
      .pipe(filter((data) => data !== undefined));
  }
  set userLoggedWebSitesAnalyse(wsa: Array<WebsiteAnalyze>) {
    this.userLoggedWebSitesAnalyseSubject.next(wsa);
  }
  get updateUserLoggedNbWebsitesObs(): Observable<boolean> {
    return this.updateUserLoggedNbWebsitesSubject.asObservable();
  }
  updateUserLoggedNbWebsites() {
    this.updateUserLoggedNbWebsitesSubject.next(true);
  }

  get isAuthenticated(): Observable<boolean> {
    if (this.router.url.slice(0, 5) !== '/demo')
      return this.userLoggedSubject.asObservable().pipe(
        filter((user) => user !== undefined),
        map((user) => !!user)
      );
  }
  registerUser(userData): Observable<any> {
    return this.httpClient.post<any>(ConstantService.apiRoute.signup, userData);
  }

  loginUser(loginData: LoginUser): Observable<LoginResponse> {
    return this.httpClient
      .post<LoginResponse>(ConstantService.apiRoute.login, loginData)
      .pipe(
        tap((response: LoginResponse) => {
          this.purgeLegacyAuthKeys();
          this.tokenStorage.setAccessToken(response.accessToken, response.userId);
        }),
      );
  }
  getCurrentUser(userId: string): Observable<any> {
    const url = `${ConstantService.apiRoute.getUserDetail}/${userId}`;
    return this.httpClient.get<any>(url);
  }
  async checkSharedWithUser(userEmail: string): Promise<boolean> {
    if (!userEmail) {
      return false;
    }
    const website = await firstValueFrom(
      this.websiteAnalyzeService.isSharedWith(userEmail),
    );
    return Array.isArray(website) && website.length > 0;
  }

  handlePostLoginRedirect(userId: string): void {
    this.getCurrentUser(userId).subscribe({
      next: async (response: any) => {
        const user = response?.user ?? response;
        if (!user?.email) {
          this.router.navigate(['/home']);
          return;
        }
        const isSharedWithUser = await this.checkSharedWithUser(user.email);
        if (user.subStatus === 'active' || isSharedWithUser) {
          this.router.navigate(['/home']);
        } else {
          this.router.navigate(['/buyPlan']);
        }
      },
      error: () => {
        this.router.navigate(['/home']);
      },
    });
  }

  async loginWithGoogle(gsiResponse, gmuId?) {
    localStorage.setItem(ConstantService.localStorageKeys.provider, 'google');
    localStorage.setItem(ConstantService.loadingUrl.device, 'mobile');
    const accessToken = this.tokenStorage.getAccessToken();
    const headers = accessToken
      ? new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        })
      : {};
    try {
      const response: any = await this.httpClient
        .post(
          ConstantService.apiRoute.registerGoogleUser +
            `${gmuId ? '?gmuId=' + gmuId : ''}`,
          {
            idToken: gsiResponse.credential,
            provider: 'google',
          },
          { headers },
        )
        .toPromise();

      this.purgeLegacyAuthKeys();
      this.tokenStorage.setAccessToken(response.accessToken, response.userId);
      localStorage.setItem(
        ConstantService.localStorageKeys.access_Token_Google,
        gsiResponse.credential,
      );

      if (response.planId) {
        window.location.href =
          window.location.origin +
          '/home/buyPlan?gmuId=' +
          gmuId +
          '&planId=' +
          response.planId;
      } else {
        this.handlePostLoginRedirect(response.userId);
      }
    } catch (error) {}
  }

  async logoutUser() {
    if (
      localStorage.getItem(ConstantService.localStorageKeys.provider) !==
      'google'
    ) {
      this.validationService.resetAllChecklists();
      this.tokenStorage.clearAuth();
      localStorage.clear();
    } else {
      const seenDemo = localStorage.getItem(
        ConstantService.localStorageKeys.seenDemo,
      );
      this.tokenStorage.clearAuth();
      localStorage.clear();
      if (!!seenDemo) {
        localStorage.setItem(
          ConstantService.localStorageKeys.seenDemo,
          seenDemo,
        );
      }
      localStorage.clear();
      this.validationService.resetAllChecklists();
    }
    this.router.navigate(['/auth/login']);
  }
}
